// 원청사 목록 화면(진행현황·평가결과·개선계획) 공용: 목록 상태 <-> URL query 직렬화/복원.
// 화면마다 spec 하나를 정의한다.
//   spec = {
//       fields: {
//           company_name: 'string',                                   // ?company_name=
//           service_type: { type: 'string', values: ['일반', '심화'] },  // 목록 밖 값은 빈 값
//           status_code: { type: 'array', values: [...] },             // ?status_code=a&status_code=b
//           requested_ts: 'date_range'                                 // ?requested_ts_start=YYYY-MM-DD&requested_ts_end=
//       },
//       perPageOptions: [50, 100],
//       defaultPerPage: 100
//   }
// spec 에 없는 키(projectid, report, rtype, pc ...)는 읽지도 지우지도 않는다. 빈 값·기본값은 URL 에서 생략한다.

const DATE_PATTERN = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
// 한쪽만 입력한 날짜 구간은 열린 구간으로 보낸다(백엔드는 시작·끝이 모두 있어야 거르고, 끝에 +1일을 더한다)
const OPEN_DATE_START = '2000-01-01';
const OPEN_DATE_END = '2999-12-31';

const fieldEntries = (spec) => Object.entries(spec.fields)
    .map(([key, field]) => [key, typeof field === 'string' ? { type: field } : field]);

const ownedKeys = (spec) => fieldEntries(spec).reduce((_keys, [key, field]) => {
    return _keys.concat(field.type === 'date_range' ? [`${key}_start`, `${key}_end`] : [key]);
}, ['current_page', 'per_page']);

const readString = (value, values) => {
    if (typeof value !== 'string' || !value.trim()) return null;
    const _value = value.trim();
    return !values || values.includes(_value) ? _value : null;
};

// 형식과 달력(2026-02-31 같은 없는 날짜 제외)을 함께 본다
const readDate = (value) => typeof value === 'string' && DATE_PATTERN.test(value)
    && new Date(`${value}T00:00:00Z`).toISOString().slice(0, 10) === value ? value : null;

export function readContractorListQuery (query, spec) {
    const filters = {};
    fieldEntries(spec).forEach(([key, field]) => {
        if (field.type === 'array') {
            const _values = [].concat(query[key] ?? []);
            filters[key] = field.values.filter(_value => _values.includes(_value));
        } else if (field.type === 'date_range') {
            filters[key] = { start: readDate(query[`${key}_start`]), end: readDate(query[`${key}_end`]) };
        } else {
            filters[key] = readString(query[key], field.values);
        }
    });
    const _page = Number(query.current_page);
    const _per_page = Number(query.per_page);
    return {
        filters,
        currentPage: Number.isInteger(_page) && _page >= 1 ? _page : 1,
        perPage: spec.perPageOptions.includes(_per_page) ? _per_page : spec.defaultPerPage
    };
}

// query 의 다른 키는 그대로 두고 spec 키만 state 로 다시 쓴다. state 는 readContractorListQuery 결과 모양(일부만 줘도 됨)
export function buildContractorListQuery (query, spec, { filters = {}, currentPage = 1, perPage = spec.defaultPerPage } = {}) {
    const _owned = ownedKeys(spec);
    const _query = Object.keys(query).reduce((_q, key) => {
        if (!_owned.includes(key)) _q[key] = query[key];
        return _q;
    }, {});
    const _filters = readContractorListQuery(toQueryValues(spec, filters), spec).filters;
    Object.assign(_query, toQueryValues(spec, _filters));
    if (currentPage > 1) _query.current_page = String(currentPage);
    if (perPage !== spec.defaultPerPage) _query.per_page = String(perPage);
    return _query;
}

// filters -> query 값(빈 값 제외)
function toQueryValues (spec, filters) {
    const _query = {};
    fieldEntries(spec).forEach(([key, field]) => {
        const _value = filters[key];
        if (field.type === 'array') {
            if (Array.isArray(_value) && _value.length) _query[key] = _value.map(String);
        } else if (field.type === 'date_range') {
            if (_value?.start) _query[`${key}_start`] = _value.start;
            if (_value?.end) _query[`${key}_end`] = _value.end;
        } else if (_value !== null && _value !== undefined && String(_value).trim()) {
            _query[key] = String(_value).trim();
        }
    });
    return _query;
}

const normalizeQuery = (query) => JSON.stringify(Object.keys(query).sort()
    .map(key => [key, [].concat(query[key]).map(_value => _value === null || _value === undefined ? null : String(_value))]));

export function isSameQuery (a, b) {
    return normalizeQuery(a) === normalizeQuery(b);
}

export function hasListFilters (spec, filters) {
    return Object.keys(buildContractorListQuery({}, spec, { filters })).length > 0;
}

export function isSameListFilters (spec, a, b) {
    return isSameQuery(buildContractorListQuery({}, spec, { filters: a || {} }), buildContractorListQuery({}, spec, { filters: b || {} }));
}

// 서버 검색 조건(condition) — 값이 없으면 null(전체 조회)
export function toSearchCondition (spec, filters) {
    const _filters = readContractorListQuery(toQueryValues(spec, filters), spec).filters;
    const condition = {};
    fieldEntries(spec).forEach(([key, field]) => {
        const _value = _filters[key];
        if (field.type === 'array') {
            if (_value.length) condition[key] = _value;
        } else if (field.type === 'date_range') {
            if (_value.start || _value.end) {
                condition[key] = { start: _value.start || OPEN_DATE_START, end: _value.end || OPEN_DATE_END };
            }
        } else if (_value) {
            condition[key] = _value;
        }
    });
    return Object.keys(condition).length ? condition : null;
}
